/** @module csi/image/_base */
define([
	"csi/_base/underscore",
	"csi/image/paths",
	"csi/locale",
	"csi/model/image",
	"csi/reflection",
	"csi/service/fileService",
	"csi/service/imageService",
	"csi/state/rootStore",
	"csi/store/Cache",
	"csi/store/Nodes",
	"csi/xml",
	"csi/xml/Query"
], function(_, imagePaths, locale, modelImage, reflection, {isImageFileName}, {isErrorImage, preloadImage, renderImage}, {sessionStore}, CacheStore, NodesStore, xml, Query) {

	const {
		DEFAULT_IMAGE_LABEL,
		IMAGE_SIZE_ICON,
		IMAGE_SIZE_SMALLEST,
		IMAGE_SIZE_SMALL,
		IMAGE_SIZE_MEDIUM,
		IMAGE_SIZE_LARGE,
		IMAGE_SIZE_LARGEST,
		IMAGE_SIZE_USE_LARGE_IMAGE,
		THUMBNAIL_IMAGE_SIZE,
		TYPE_IMAGE,
		getDefaultImage,
		getImageAttributeBySize,
		getImageContainerNumberedKeys,
		getImageLabeledKeys,
		getImageSizeMaxHeight,
		getImageSrc,
	} = modelImage;

	const CSS_VIEWABLE = "attrImageLargest";

	const CSS = {
		[IMAGE_SIZE_ICON]: "attrImageIcon",
		[IMAGE_SIZE_SMALLEST]: "attrImageSmallest",
		[IMAGE_SIZE_SMALL]: "attrImageSmall",
		[IMAGE_SIZE_MEDIUM]: "attrImageMedium",
		[IMAGE_SIZE_LARGE]: "attrImageLarge"
	};

	const SIZE_LARGE = {
		[IMAGE_SIZE_LARGE]: true,
		[IMAGE_SIZE_LARGEST]: true,
		[IMAGE_SIZE_USE_LARGE_IMAGE]: true
	};

	const SORT_SORTDATENEWEST = "SortDateNewest";
	const SORT_SORTDATEOLDEST = "SortDateOldest";
	const SORT_SORTNAMEAZ = "SortNameAZ";
	const SORT_SORTNAMEZA = "SortNameZA";
	const DATE_ADDED = "DateAdded";
	const FILE = "File";

	const imageSortingData = [
		{
			attribute: DATE_ADDED,
			attributeId: reflection.ATTR_MODIFIED_AT,
			descending: true,
			key: SORT_SORTDATENEWEST,
		},
		{
			attribute: DATE_ADDED,
			attributeId: reflection.ATTR_MODIFIED_AT,
			key: SORT_SORTDATEOLDEST,
		},
		{
			attribute: FILE,
			attributeId: xml.ATTR_NAME,
			key: SORT_SORTNAMEAZ,
		},
		{
			attribute: FILE,
			attributeId: xml.ATTR_NAME,
			descending: true,
			key: SORT_SORTNAMEZA,
		},
	];

	// Store: store to act as cache for all images
	// TODO: should CacheStore have limit after which it starts removing nodes used long ago
	const IMAGE_CACHE = new CacheStore();

	// Nodes: nodes store for user uploads
	let _uploadsStore = null;

	return {
		create: renderImage,
		getImageSizeMaxHeight,
		getImageSrc,
		getNumberedKeys: getImageContainerNumberedKeys,
		isError: isErrorImage,
		isValidFormat: isImageFileName,
		preload: preloadImage,
		SORT_SORTDATENEWEST,
		SORT_SORTDATEOLDEST,
		SORT_SORTNAMEAZ,
		SORT_SORTNAMEZA,
		imageSortingData,

		/**
		 * Return nodes store containing current user's image uploads
		 * @param {string} [imageSort]
		 */
		getUploadsStore(imageSort) {
			const {userPreferencesUrl, userUrl} = sessionStore;
			const query = new Query();
			query.Attr("IgnoreInSearch", xml.OP_EQUALS, false);
			query.RefAttr("UploadedByUser", xml.OP_EQUALS, userUrl);
			const defaultKey = imageSort || SORT_SORTDATENEWEST;
			const defaultSort = imageSortingData.find(item => item.key === defaultKey);
			const { attributeId, descending } = defaultSort;
			_uploadsStore = _uploadsStore || new NodesStore({
				bAddToFront: true,
				cachingStore: IMAGE_CACHE,
				filterQuery: query,
				userPreferencesUrl,
				requestParameters: {
					"Ext.File.Info": true
				},
				sortQuery: new Query().SortByAttr(attributeId, null, null, descending),
				typeNode: TYPE_IMAGE,
			});

			return _uploadsStore;
		},

		getDefaultImage,

		/**
		 * Return path to the default image
		 * @param {string} path
		 * @return {string} - path to the default image
		 */
		getDefaultImagePath: function(path) {
			return xml.path(path, reflection.ATTR_IMAGES, xml.PATH_OP_INDEX, DEFAULT_IMAGE_LABEL);
		},

		getImageCacheStore: function() {
			return IMAGE_CACHE;
		},

		getImageIdByContext: function(/*Context*/ctx) {
			// summary: return attribute id holding img src by size enum
			var enumSize = ctx.prefImageSize;

			if (ctx.bPrint && SIZE_LARGE[enumSize] && sessionStore.printViewableImage) {
				return reflection.ATTR_VIEWABLE;
			}

			return getImageAttributeBySize(enumSize) || reflection.ATTR_VIEWABLE;
		},

		getImageLabelName: function(/*Node*/node, /*String*/key, /*String*/attrId) {
			// summary: find localized name for image label
			if ((attrId != reflection.ATTR_IMAGES) || (key == null)) {
				return key;
			}

			if (_.isNumber(key)) {
				// key is numbered, so return comma-separated list of labels for this image
				var keys = getImageLabeledKeys(node, key);
				var labels = _.map(keys, function(labeledKey) {
					return this.getImageLabelName(node, labeledKey, attrId);
				}, this);

				return labels.join(", ");
			} else if (key) {
				// key is a label, so just return its name
				var rc = reflection.getNodeClass(node);
				var rcEnum = rc && rc.imageLabelEnum;
				if (rcEnum) {
					return locale.getEnumName(rcEnum.getValue(key));
				}
			}

			return null;
		},

		/**
		 * Return node store containing image nodes related to given node
		 * @param {C8Node} node 
		 * @return {Nodes}
		 */
		getNodeImageStore(node) {
			const {$Type, $URL} = node;
			const paths = imagePaths.get($Type);

			if (paths.length) {
				return new NodesStore({
					cachingStore: IMAGE_CACHE,
					filterQuery: new Query().Attr("IgnoreInSearch", xml.OP_EQUALS, false),
					requestParameters: {
						"Ext.File.Info": true,
						"Qry.Limit.Path": paths.map((path) => xml.getUrlPath($URL, path))
					},
					sortQuery: new Query().SortByAttr(reflection.ATTR_MODIFIED_AT, null, null, true),
					typeNode: TYPE_IMAGE,
				});
			}

			return new NodesStore({
				data: [],
			});
		},

		/**
		 * Return CSS class to use for given image size
		 * @param {string} imageSize
		 * @return {string}
		 */
		getImageSizeCss: function(imageSize) {
			return CSS[imageSize] || CSS_VIEWABLE;
		},

		getImageMapDifference: function(/*Object*/imageMap, /*String[]*/newUrls, /*Boolean*/bSetDefault) {
			// summary: determine new mappings to add to imageMap based on new images
			// summary: increment largest lookup key to get next key
			var key = Number(this.getLastKey(imageMap) || 0) + 1;
			var urls = this.getNumberedValues(imageMap);
			var urlmap = {};

			_.each(newUrls, function(url, i) {
				// do not add image if already in the container mapped to numbered key
				if (urls && (_.indexOf(urls, url) >= 0)) {
					return;
				}

				// set default if not already set - only need to do this the first time
				if (bSetDefault && !i && imageMap && !xml.isValidURL(imageMap[DEFAULT_IMAGE_LABEL])) {
					urlmap[DEFAULT_IMAGE_LABEL] = url;
				}

				urlmap[i ? (key + i) : key] = url;
			}, this);

			return urlmap;
		},

		/**
		 * Return numbered key corresponding to labeled image
		 * @param {Record<string, cnl>} obj 
		 * @param {cnl} url 
		 * @return {string} 
		 */
		getNumberedKey(obj, url) {
			if (obj) {
				for (var key in obj) {
					if (Number(key) && (obj[key] == url)) {
						return key;
					}
				}
			}

			return null;
		},

		getNumberedValues: function(/*Object*/obj, /*Boolean?*/bDefaultOmit, /*String?*/omitKey) {
			// summary: return array of numbered (non-labels) keys omitting one if given
			return _.values(_.pick(obj, this.getNumberedKeys(obj, bDefaultOmit, omitKey)));
		},

		getFirstKey: function(/*Object*/obj, /*Boolean?*/bDefaultOmit) {
			// summary: find smallest numbered key in object
			var keys = this.getNumberedKeys(obj, bDefaultOmit);
			return keys.length ? keys[0] : null;
		},

		getLastKey: function(/*Object*/obj) {
			// summary: find largest numbered key in object
			var keys = this.getNumberedKeys(obj);
			return keys.length ? keys[keys.length - 1] : null;
		},

		/**
		 * Return height of image scaled within given bounds
		 * @param {C8Node} imageNode 
		 * @param {number} maxHeight 
		 * @param {number} maxWidth 
		 * @return {number}
		 */
		getScaledImageHeight: function(imageNode, maxHeight, maxWidth) {
			let height = imageNode.Height;
			let width = imageNode.Width;
			let imageAspectRatio = height / width;

			if ((height > THUMBNAIL_IMAGE_SIZE) && (maxHeight <= THUMBNAIL_IMAGE_SIZE)) {
				// determine width of thumbnail; it will have an integer number of pixels which can affect predicted height by a pixel
				width = Math.ceil(width * THUMBNAIL_IMAGE_SIZE / height);
				height = THUMBNAIL_IMAGE_SIZE;
				imageAspectRatio = height / width;
			}

			const cellAspectRatio = maxHeight / maxWidth;
			const predictedHeight = (cellAspectRatio <= imageAspectRatio) ?
				Math.min(height, maxHeight) :
				Math.ceil(maxWidth * imageAspectRatio);

			return predictedHeight;
		}
	};
});